import { Autocomplete, Box, FormControl, IconButton, MenuItem, Modal, Select, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography } from "@mui/material";
import api from "services/api";
import { RootState } from "@services/store";
import { Annotation, Inject, PdfViewerComponent, TextSelection, TextSearch, Navigation, Toolbar, Magnification, ThumbnailView, FormFields, FormDesigner, Print } from "@syncfusion/ej2-react-pdfviewer";
import { IDoctypeField, putDoctype } from "features/doctypesSlice";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { usePdfViewer, blobToBase64 } from "context/PdfViewerContext";
import { PDFDocument } from "pdf-lib";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import { createWorker } from "tesseract.js";
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useTranslation } from "react-i18next";
import dayjs, { Dayjs } from "dayjs";
import CancelIcon from '@mui/icons-material/Cancel';
import SaveIcon from '@mui/icons-material/Save';
import { useSnackbar } from "context/SnackbarContext";
import { useDialog } from "context/DialogContext";
import { calculateRatio } from "services/images";

interface IOcrTemplateItem {
    Pos: number,
    OcrType: string,
    Page: number,
    ValType: string,
    Value: string, 
    InputValue: string,
    Lib: string,
    Left: number,
    Top: number,
    Width: number,
    Height: number,
    ValFormat: string
}

const TemplateCreator = ({ onClose }: any) => {
    const dispatch = useDispatch();
    const { showSnackbar } = useSnackbar();
    const { t } = useTranslation();
    const { showDialog } = useDialog();
    const selectedDoctypeKey = useSelector((state: RootState) => state.doctypes.selectedDoctypeKey);
    const doctype = useSelector((state: RootState) => state.doctypes.doctypes.find((doc) => doc.Key === selectedDoctypeKey));
    const userConfig = useSelector((state: RootState) => state.user);
    
    const [ hasChanges, setHasChanges ] = useState<boolean>(false);
    const [ selectedPos, setSelectedPos ] = useState<any>(-1);
    const [ zones, setZones ] = useState<any[]>([]);
    const [ pdfviewer, setPdfViewer ] = useState<any>(React.createRef());
    const { pdfViewer: mainPdfViewer } = usePdfViewer();
    const [ deleteBtnVisible, setDeleteBtnVisible ] = useState<boolean>(false);

    const docTypeTitle = doctype?.Title;
    const fieldRef = doctype?.Fields.find((field) => field.Pos === doctype.PosRef);

    // Fonctions de chargement

    const loadDocument = async () => {
        if(mainPdfViewer && pdfviewer.current) {
            const blob = await mainPdfViewer.saveAsBlob();
            const buffer = await blob.arrayBuffer();
            
            const pdf = await PDFDocument.load(buffer);

            if(mainPdfViewer.currentPageNumber > 1) {
                for(let i = mainPdfViewer.currentPageNumber - 1; i >= 1; i--) {
                    pdf.removePage(i - 1);
                }
            }

            const newPdf = await pdf.save();
            const newBlob = new Blob([newPdf], { type: "application/pdf" });
            
            const base64 = await blobToBase64(newBlob);
            pdfviewer.current.dataBind();

            setTimeout(() => {
                pdfviewer.current.load(base64, null);
            }, 500);
        }
    }

    const onDocumentLoaded = () => {
        loadTemplate();
    }

    const loadTemplate = async () => {
        try {
            const template = await api.get(`/ocrtemplates/creator/${doctype?.Key}${fieldRef ? `_${fieldRef?.InputValue}` : ""}`);
            if(template.status === 200) {
                let newZones: any[] = [];

                template.data.Data.Zones.forEach((zn: IOcrTemplateItem) => {
                    const field = doctype?.Fields.find((fld: IDoctypeField) => fld.Pos === zn.Pos);
                    let value = (fieldRef && fieldRef.Pos == zn.Pos) ? fieldRef.InputValue : zn.Value;
                    let inputValue = value;

                    if(field) {
                        if(field.Type === "AUTOCOMPLETE" && field.Options) {
                            field.Options.forEach((opt: any) => {
                                if(opt.Value == value) {
                                    inputValue = opt.Label;
                                }
                            });
                        }
                    }

                    newZones.push({
                        ref: React.createRef(),
                        hasError: false,
                        helperText: undefined,
                        pos: zn.Pos,
                        ocrType: zn.OcrType,
                        page: zn.Page,
                        value: value, 
                        defaultValue: value, 
                        inputValue: inputValue,
                        left: zn.Left,
                        top: zn.Top,
                        width: zn.Width,
                        height: zn.Height,
                    });

                    //newZones[newZones.length - 1] = checkPos(newZones[newZones.length - 1]);

                    if(zn.OcrType == "C" || zn.OcrType == "CTRL") {
                        if(zn.Page <= pdfviewer.current.pageCount && zn.Width > 0 && zn.Height > 0) {
                            const options: any = {
                                offset: { x: zn.Left, y: zn.Top },
                                pageNumber: zn.Page,
                                width: zn.Width,
                                height: zn.Height,
                                opacity: 0.3,
                                strokeColor: '#366193',
                                fillColor: '#5ebff1',
                                thickness: 0,
                                borderDashArray: 0,
                                borderStyle: "",
                                author: `eDocIndexWebTemplateCreator|${zn.Pos}`
                            };
                
                            pdfviewer.current.annotation.addAnnotation('Rectangle', options);
                        }
                    }

                    let isCreationMode = true;
                    
                    if(doctype) {
                        if(doctype?.PosRef == 1) {
                            isCreationMode = doctype.PosRefValues.find((value: string) => value == doctype.Key) ? false : true;
                        } else if(doctype?.PosRef > 1) {
                            isCreationMode = doctype.PosRefValues.find((value: string) => value == doctype.Fields.find((fld: IDoctypeField) => fld.Pos == doctype.PosRef)?.InputValue?.toLowerCase()) ? false : true;
                        }
                    }

                    if(!isCreationMode) {
                        setDeleteBtnVisible(true);
                    }
                });

                setZones(newZones);
            }
        } catch (error: any) {
            return null;
        }
    }

    // Fonctions OCR

    const generateOcr = async(zone: any) => {
        if(zone.page <= pdfviewer.current.pageCount) {
            const viewerPageSize = pdfviewer.current.viewerBase.pageSize[zone.page - 1];
            const baseRatio = viewerPageSize.height / viewerPageSize.width;
            const img = await pdfviewer.current.exportAsImage(zone.page - 1, { width: 1000, height: 1000 * baseRatio } as any);
            const ratio = await calculateRatio(pdfviewer.current, (zone.page - 1), img);
            const worker = await createWorker(userConfig.ocrLangs);

            const data = await worker.recognize(img, { rectangle: {
                left: zone.left * ratio,
                top: zone.top * ratio,
                height: zone.height * ratio,
                width: zone.width * ratio,
            } });
            
            worker.terminate();

            return data.data.text.replace(/\r\n|\r|\n/g, " ").trim();
        } else {
            return "";
        }
    }

    // Fonctions de validation

    const checkPos = (zoneItem: any) => {
        if(doctype) {
            const field = doctype.Fields.find((fld: IDoctypeField) => fld.Pos === zoneItem.pos);

            if(field && zoneItem) {
                let hasError = false;
                let errorMsg = "";

                if(zoneItem.ocrType == "V") {
                    if(field.Required && (!zoneItem.value || zoneItem.value == "")) {
                        hasError = true;
                        errorMsg = t("validation.required");
                    } else if(zoneItem.value && zoneItem.value.includes("|")) {
                        hasError = true;
                        errorMsg = t("validation.pipeUsageNotAutorised");
                    } else {
                        if(field.Type === "NUMBER") {
                            if (!(/^-?\d*\.?\d*$/.test(zoneItem.value))) {
                                hasError = true;
                                errorMsg = t("validation.notANumber");
                            }
                        } else if(field.Type === "DATE") {
                            const isValidDate = dayjs(zoneItem.value, "DD.MM.YYYY", true).isValid();
                            if (!isValidDate && zoneItem.value !== "") {
                                hasError = true;
                                errorMsg = t("validation.dateInvalid");
                            }
                        }
                    }
                }

                zoneItem.hasError = hasError;
                zoneItem.helperText = errorMsg;
            }
        }

        return zoneItem;
    }

    // Fonctions d'interface

    const onLineClick = (pos: number) => {
        if(pos === doctype?.PosRef) {
            return;
        }

        pdfviewer.current.annotationModule.clearSelection();

        pdfviewer.current.annotationCollection.forEach((annot: any) => {
            if(annot.author == `eDocIndexWebTemplateCreator|${pos}`) {
                pdfviewer.current.annotationModule.selectAnnotation(annot.annotationId);
            }
        });

        setSelectedPos(pos);
    }

    const zoneReset = (pos: number) => {
        let zonesObject = zones.slice();
        let selectedZoneIndex = zonesObject.findIndex((zone: any) => zone.pos == pos);

        if(selectedZoneIndex >= 0) {
            zonesObject[selectedZoneIndex].hasError = false;
            zonesObject[selectedZoneIndex].helperText = undefined;
            zonesObject[selectedZoneIndex].ocrType = "";
            zonesObject[selectedZoneIndex].page = 0;
            zonesObject[selectedZoneIndex].value = ""; 
            zonesObject[selectedZoneIndex].left = 0;
            zonesObject[selectedZoneIndex].top = 0;
            zonesObject[selectedZoneIndex].width = 0;
            zonesObject[selectedZoneIndex].height = 0;

            pdfviewer.current.annotationCollection.forEach((annot: any) => {
                if(annot.author == `eDocIndexWebTemplateCreator|${pos}`) {
                    pdfviewer.current.annotationModule.deleteAnnotationById(annot.annotationId);
                }
            });

            setZones(zonesObject);
            setHasChanges(true);
        }
    }

    const resetCoordinates = async (pos: number) => {
        let zonesObject = zones.slice();
        let selectedZoneIndex = zonesObject.findIndex((zone: any) => zone.pos == pos);

        if(selectedZoneIndex >= 0) {
            zonesObject[selectedZoneIndex].page = pdfviewer.current.currentPageNumber;
            zonesObject[selectedZoneIndex].left = 10;
            zonesObject[selectedZoneIndex].top = 10;
            zonesObject[selectedZoneIndex].height = 50;
            zonesObject[selectedZoneIndex].width = 50;

            pdfviewer.current.annotationCollection.forEach((annot: any) => {
                if(annot.author == `eDocIndexWebTemplateCreator|${pos}`) {
                    pdfviewer.current.annotationModule.deleteAnnotationById(annot.annotationId);
                }
            });

            const options: any = {
                offset: { x: zonesObject[selectedZoneIndex].left, y: zonesObject[selectedZoneIndex].top },
                pageNumber: zonesObject[selectedZoneIndex].page,
                width: zonesObject[selectedZoneIndex].width,
                height: zonesObject[selectedZoneIndex].height,
                opacity: 0.3,
                strokeColor: '#366193',
                fillColor: '#5ebff1',
                thickness: 0,
                borderDashArray: 0,
                borderStyle: "",
                author: `eDocIndexWebTemplateCreator|${zonesObject[selectedZoneIndex].pos}`
            };

            pdfviewer.current.annotation.addAnnotation('Rectangle', options);
            pdfviewer.current.annotationCollection.forEach((annot: any) => {
                if(annot.author == `eDocIndexWebTemplateCreator|${pos}`) {
                    pdfviewer.current.annotationModule.selectAnnotation(annot.annotationId);
                }
            });

            if(zonesObject[selectedZoneIndex].pos === 0) {
                zonesObject[selectedZoneIndex].value = await generateOcr(zonesObject[selectedZoneIndex]);
            }

            setZones(zonesObject);
            setHasChanges(true);
        }
    }

    // Fonctions annotations

    const onAnnotationMove = async (args: any) => {
        let zonesObject = zones.slice();
        let selectedZoneIndex = zonesObject.findIndex((zone: any) => zone.pos == selectedPos);

        if(selectedZoneIndex >= 0) {
            zonesObject[selectedZoneIndex].left = args.currentPosition.x;
            zonesObject[selectedZoneIndex].top = args.currentPosition.y;
            zonesObject[selectedZoneIndex].width = args.currentPosition.width;
            zonesObject[selectedZoneIndex].height = args.currentPosition.height;

            if(zonesObject[selectedZoneIndex].pos === 0) {
                zonesObject[selectedZoneIndex].value = await generateOcr(zonesObject[selectedZoneIndex]);
            }

            setZones(zonesObject);
            setHasChanges(true);
        }
    }

    const onAnnotationResize = async (args: any) => {
        let zonesObject = zones.slice();
        let selectedZoneIndex = zonesObject.findIndex((zone: any) => zone.pos == selectedPos);
        
        if(selectedZoneIndex >= 0) {
            zonesObject[selectedZoneIndex].left = args.annotationBound.x;
            zonesObject[selectedZoneIndex].top = args.annotationBound.y;
            zonesObject[selectedZoneIndex].width = args.annotationBound.width;
            zonesObject[selectedZoneIndex].height = args.annotationBound.height;
            
            if(zonesObject[selectedZoneIndex].pos === 0) {
                zonesObject[selectedZoneIndex].value = await generateOcr(zonesObject[selectedZoneIndex]);
            }

            setZones(zonesObject);
            setHasChanges(true);
        }
    }

    const onAnnotationSelect = (args: any) => {
        /*if(args.annotationCollection) {
            const regexp = /eDocIndexWebTemplateCreator\|(?<pos>[0-9]+)/g;

            args.annotationCollection.forEach((annot: any) => {
                const matches = annot.author.matchAll(regexp);

                for (const match of matches) {
                    setSelectedPos(match.groups.pos);
                    return;
                }
            });
        }*/

        if(pdfviewer.current.selectedItems.annotations) {
            const regexp = /eDocIndexWebTemplateCreator\|(?<pos>[0-9]+)/g;

            pdfviewer.current.selectedItems.annotations.forEach((annot: any) => {
                const matches = annot.properties.author.matchAll(regexp);

                for (const match of matches) {
                    setSelectedPos(match.groups.pos);
                    return;
                }
            });
        }
    }

    // Fonctions du formulaire

    const onOcrTypeChanged = (args: any, pos: number) => {
        let zonesObject = zones.slice();
        let selectedZoneIndex = zonesObject.findIndex((zone: any) => zone.pos == pos);

        if(selectedZoneIndex < 0) {
            return;
        }

        zonesObject[selectedZoneIndex].ocrType = args.target.value;

        switch(args.target.value) {
            case "C":
                zonesObject[selectedZoneIndex].page = pdfviewer.current.currentPageNumber;
                zonesObject[selectedZoneIndex].left = 10;
                zonesObject[selectedZoneIndex].top = 10;
                zonesObject[selectedZoneIndex].width = 50;
                zonesObject[selectedZoneIndex].height = 50;
                zonesObject[selectedZoneIndex].value = "";

                const options: any = {
                    offset: { x: zonesObject[selectedZoneIndex].left, y: zonesObject[selectedZoneIndex].top },
                    pageNumber: zonesObject[selectedZoneIndex].page,
                    width: zonesObject[selectedZoneIndex].width,
                    height: zonesObject[selectedZoneIndex].height,
                    opacity: 0.3,
                    strokeColor: '#366193',
                    fillColor: '#5ebff1',
                    thickness: 0,
                    borderDashArray: 0,
                    borderStyle: "",
                    author: `eDocIndexWebTemplateCreator|${zonesObject[selectedZoneIndex].pos}`
                };
    
                pdfviewer.current.annotation.addAnnotation('Rectangle', options);
                pdfviewer.current.annotationCollection.forEach((annot: any) => {
                    if(annot.author == `eDocIndexWebTemplateCreator|${pos}`) {
                        pdfviewer.current.annotationModule.selectAnnotation(annot.annotationId);
                    }
                });

                break;
            case "V":
                zonesObject[selectedZoneIndex].page = 0;
                zonesObject[selectedZoneIndex].left = 0;
                zonesObject[selectedZoneIndex].top = 0;
                zonesObject[selectedZoneIndex].width = 0;
                zonesObject[selectedZoneIndex].height = 0;
                zonesObject[selectedZoneIndex].value = "";

                pdfviewer.current.annotationCollection.forEach((annot: any) => {
                    if(annot.author == `eDocIndexWebTemplateCreator|${pos}`) {
                        pdfviewer.current.annotationModule.deleteAnnotationById(annot.annotationId);
                    }
                });

                break;
            case "":
                zonesObject[selectedZoneIndex].page = 0;
                zonesObject[selectedZoneIndex].left = 0;
                zonesObject[selectedZoneIndex].top = 0;
                zonesObject[selectedZoneIndex].width = 0;
                zonesObject[selectedZoneIndex].height = 0;
                zonesObject[selectedZoneIndex].value = "";

                pdfviewer.current.annotationCollection.forEach((annot: any) => {
                    if(annot.author == `eDocIndexWebTemplateCreator|${pos}`) {
                        pdfviewer.current.annotationModule.deleteAnnotationById(annot.annotationId);
                    }
                });

                break;
        }

        zonesObject[selectedZoneIndex] = checkPos(zonesObject[selectedZoneIndex]);

        setZones(zonesObject);
        setHasChanges(true);
        onLineClick(pos);
    }

    const onNumberValueChanged = (args: any, pos: number) => {
        if (/^-?\d*\.?\d*$/.test(args.target.value)) {
            let zonesObject = zones.slice();
            let selectedZoneIndex = zonesObject.findIndex((zone: any) => zone.pos == pos);

            if(selectedZoneIndex >= 0) {
                zonesObject[selectedZoneIndex].value = args.target.value;
                zonesObject[selectedZoneIndex] = checkPos(zonesObject[selectedZoneIndex]);
                setZones(zonesObject);
                setHasChanges(true);
            }
        }
    }

    const onNumberBlur = (args: any, pos: number) => {
        let zonesObject = zones.slice();
        let selectedZoneIndex = zonesObject.findIndex((zone: any) => zone.pos == pos);

        if(selectedZoneIndex >= 0 && zonesObject[selectedZoneIndex].value && zonesObject[selectedZoneIndex].value != "") {
            zonesObject[selectedZoneIndex].value = parseFloat(zonesObject[selectedZoneIndex].value).toFixed(2);
            setZones(zonesObject);
            setHasChanges(true);
        }
    }

    const onDateTextValueChanged = (args: any, pos: number) => {
        let zonesObject = zones.slice();
        let selectedZoneIndex = zonesObject.findIndex((zone: any) => zone.pos == pos);

        if(selectedZoneIndex >= 0) {
            zonesObject[selectedZoneIndex].value = args.target.value;
            zonesObject[selectedZoneIndex] = checkPos(zonesObject[selectedZoneIndex]);
            setZones(zonesObject);
            setHasChanges(true);
        }
    }

    const onDateValueChanged = (args: Dayjs, pos: number) => {
        let zonesObject = zones.slice();
        let selectedZoneIndex = zonesObject.findIndex((zone: any) => zone.pos == pos);

        if(selectedZoneIndex >= 0) {
            zonesObject[selectedZoneIndex].value = args ? args.format("DD.MM.YYYY") : "";
            zonesObject[selectedZoneIndex] = checkPos(zonesObject[selectedZoneIndex]);
            setZones(zonesObject);
            setHasChanges(true);
        }
    }

    const onAutocompleteValueChanged = (args: any, newValue: any, pos: number) => { }

    const onAutocompleteInputValueChanged = (args: any, newValue: any, pos: number, fixed: boolean) => {
        let hasChanged = false;
        let zonesObject = zones.slice();
        let selectedZoneIndex = zonesObject.findIndex((zone: any) => zone.pos == pos);
        const field = doctype?.Fields.find((fld: IDoctypeField) => fld.Pos == pos);

        if(selectedZoneIndex < 0) {
            return;
        }

        if(field) {
            if(field.Fixed && field.Options.length > 0) {
                const option = field.Options.find((opt: any) => opt.Value == newValue || opt.Label == newValue);
                if(option) {
                    if(zonesObject[selectedZoneIndex].value != option.Value) {
                        zonesObject[selectedZoneIndex].value = option.Value;
                        hasChanged = true;
                    }

                    if(zonesObject[selectedZoneIndex].inputValue != option.Label) {
                        zonesObject[selectedZoneIndex].inputValue = option.Label;
                        hasChanged = true;
                    }
                } else {
                    newValue = "";
                }
            } else if(!field.Fixed && field.Options.length > 0) {
                const option = field.Options.find((opt: any) => opt.Value == newValue || opt.Label == newValue);
                if(option) {
                    if(zonesObject[selectedZoneIndex].value != option.Value) {
                        zonesObject[selectedZoneIndex].value = option.Value;
                        hasChanged = true;
                    }

                    if(zonesObject[selectedZoneIndex].inputValue != option.Label) {
                        zonesObject[selectedZoneIndex].inputValue = option.Label;
                        hasChanged = true;
                    }
                } else if(zonesObject[selectedZoneIndex].inputValue != newValue) {
                    zonesObject[selectedZoneIndex].inputValue = newValue;
                    hasChanged = true;
                }
            } else {
                if(zonesObject[selectedZoneIndex].value != newValue) {
                    zonesObject[selectedZoneIndex].value = newValue;
                    hasChanged = true;
                }

                if(zonesObject[selectedZoneIndex].inputValue != newValue) {
                    zonesObject[selectedZoneIndex].inputValue = newValue;
                    hasChanged = true;
                }
            }
        }

        if(!newValue || newValue == "") {
            if(zonesObject[selectedZoneIndex].value != "") {
                zonesObject[selectedZoneIndex].value = "";
                hasChanged = true;
            }

            if(zonesObject[selectedZoneIndex].inputValue != "") {
                zonesObject[selectedZoneIndex].inputValue = "";
                hasChanged = true;
            }
        } 

        if(hasChanged) {
            zonesObject[selectedZoneIndex] = checkPos(zonesObject[selectedZoneIndex]);
            setZones(zonesObject);
            setHasChanges(true);
        }
    }

    const saveTemplate = async () => {
        try {
            let zonesCopy = zones.slice();
            for(let i = 0; i < zonesCopy.length; i++) {
                zonesCopy[i] = checkPos(zonesCopy[i]);
            }
            setZones(zonesCopy);

            const zoneError = zonesCopy.find((zn: any) => zn.hasError);

            if(zoneError) {
                showSnackbar({
                    message: t("error.errorsInForm"),
                    severity: "error",
                });

                return;
            }

            let zonesToSave: IOcrTemplateItem[] = [];
            zones.forEach((zn: any) => {
                zonesToSave.push({
                    Pos: zn.pos,
                    OcrType: zn.ocrType,
                    Page: zn.page,
                    ValType: "",
                    Value: zn.value, 
                    InputValue: "",
                    Lib: "",
                    Left: zn.left,
                    Top: zn.top,
                    Width: zn.width,
                    Height: zn.height,
                    ValFormat: ""
                });
            });

            const res = await api.post("/ocrtemplates/" + doctype?.Key + (fieldRef?.InputValue ? "_" + fieldRef.InputValue : ""), {
                zones: zonesToSave,
            });

            if(res.status === 200) {
                setHasChanges(false);
                setDeleteBtnVisible(true);

                let newTemplateCreated = false;
                let newPosRefValue = "";

                if(doctype) {
                    if(fieldRef && fieldRef.InputValue) {
                        const posRefValue = doctype.PosRefValues.find((value: any) => value === fieldRef.InputValue);
                        if(!posRefValue) {
                            newTemplateCreated = true;
                            newPosRefValue = fieldRef.InputValue.toLowerCase().trim();
                        }
                    } else {
                        const posRefValue = doctype.PosRefValues.find((value: any) => value === doctype.Key);
                        if(!posRefValue) {
                            newTemplateCreated = true;
                            newPosRefValue = doctype.Key;
                        }
                    }

                    if(newTemplateCreated) {
                        let doctypeCopy = structuredClone(doctype);
                        doctypeCopy.PosRefValues.push(newPosRefValue);
                        dispatch(putDoctype(doctypeCopy));
                    }
                }

                showSnackbar({
                    message: res.data.Message,
                    severity: "success",
                });
            }
        } catch (error: any) {
            showSnackbar({
                message: error.data ? error.data.Message || t("internalError") : t("internalError"),
                severity: "error",
            });
        }
    };

    const onDeleteTemplate = async () => {
        await showDialog({
            type: "confirmation",
            title: t("confirmation.title"),
            message: t("dialog.wantToDeleteTemplate"),
            severity: "warning",
            onConfirm: async () => {
                try {
                    const res = await api.delete("/ocrtemplates/" + doctype?.Key + (fieldRef?.InputValue ? "_" + fieldRef.InputValue : ""), { });
        
                    if(res.status === 200) {
                        let posRefValueToRemove = -1;

                        if(doctype) {
                            if(fieldRef && fieldRef.InputValue) {
                                posRefValueToRemove = doctype.PosRefValues.findIndex((value: any) => value === fieldRef.InputValue.toLowerCase());
                            } else {
                                posRefValueToRemove = doctype.PosRefValues.findIndex((value: any) => value === doctype.Key);
                            }

                            if(posRefValueToRemove >= 0) {
                                let doctypeCopy = structuredClone(doctype);
                                doctypeCopy.PosRefValues.splice(posRefValueToRemove, 1);
                                dispatch(putDoctype(doctypeCopy));
                            }
                        }

                        showSnackbar({
                            message: res.data.Message,
                            severity: "success",
                        });

                        onClose(false);
                    }
                } catch (error: any) {
                    showSnackbar({
                        message: error.data ? error.data.Message || t("internalError") : t("internalError"),
                        severity: "error",
                    });
                }
            },
            onCancel: () => {},
        });
    }

    return (
        <Modal open={true} 
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description">
            <Box
                sx={{
                    position: "absolute",
                    boxShadow: 24,
                    bgcolor: "background.paper",
                    width: "94%",
                    left: "3%",
                    top: '3%',
                    height: '94%',
                    display: "flex",
                    flexDirection: "column"
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        pl: 3,
                        pt: 3,
                        pr: 2,
                        pb: 2,
                        backgroundColor: "#366193",
                        color: "white"
                    }}
                >
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="body1" sx={{ fontWeight: "bold", display: "inline" }}>
                            {t("template")} :
                        </Typography>
                        <Typography variant="h5" sx={{ display: "inline", ml: 1 }}>
                            {docTypeTitle}{fieldRef ? ` - ${fieldRef.InputValue}` : `` }                        
                        </Typography>
                    </Box>

                    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                        <Tooltip title="Fermer" arrow placement="top">
                            <IconButton sx={{ color: "white" }} onClick={() => onClose(hasChanges)}>
                                <CloseIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>

                <Box
                    sx={{
                        p: 2,
                        bgcolor: "background.paper",
                        borderTop: "1px solid #ddd",
                        borderBottom: "1px solid #ddd",
                        flex: 1,
                        display: 'flex', 
                        flexDirection: 'row',
                        overflow: "auto"
                    }}
                >
                    <Box sx={{ height: "100%", overflow: "hidden", flex: 1, pr: 1 }}>
                        <TableContainer component="div" sx={{ height: "100%", overflow: "auto", borderRadius: 0, border: 0 }}>
                            <Table stickyHeader sx={{ minWidth: 0 }} size="small" aria-label="simple table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ m: 0, p: 1, backgroundColor: "#101010", color: "#f0f0f0" }}>{t("templateCreator.label")}</TableCell>
                                        <TableCell sx={{ m: 0, p: 1, backgroundColor: "#101010", color: "#f0f0f0" }}>{t("templateCreator.type")}</TableCell>
                                        <TableCell sx={{ m: 0, p: 1, backgroundColor: "#101010", color: "#f0f0f0" }}>#</TableCell>
                                        <TableCell sx={{ m: 0, p: 1, backgroundColor: "#101010", color: "#f0f0f0" }}>{t("templateCreator.readingArea")} / {t("templateCreator.value")}</TableCell>
                                        <TableCell sx={{ m: 0, p: 1, backgroundColor: "#101010", color: "#f0f0f0" }}></TableCell>
                                        <TableCell sx={{ m: 0, p: 1, backgroundColor: "#101010", color: "#f0f0f0" }}></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                { zones.sort((a: any, b: any) => {
                                    return a.pos < b.pos ? -1 : 0;
                                }).sort((a: any, b: any) => {
                                    return a.pos === doctype?.PosRef ? -1 : 0;
                                }).map((zone: any) => {
                                    const field = doctype?.Fields.find((fld: IDoctypeField) => fld.Pos == zone.pos);
                                    
                                    const valueInputComponent = field?.Type === "AUTOCOMPLETE" ? (
                                        <Autocomplete 
                                            componentsProps={{ popper: { style: { width: 'fit-component' }, placement: 'bottom-start' } }} 
                                            openOnFocus
                                            freeSolo={field.Customizable || !field.Fixed}
                                            options={field.Options || []}
                                            getOptionLabel={(option: any) => typeof option === "string" ? option : option.Label }
                                            noOptionsText={t("fieldNoOptions")}
                                            value={zone.value}
                                            inputValue={zone.inputValue}
                                            popupIcon={<ExpandMoreIcon />}
                                            forcePopupIcon={field && field.Options && field.Options.length > 0}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    inputRef={zone.ref ?? null}
                                                    label=""
                                                    variant="standard"
                                                    required={field.Required}
                                                    error={zone.hasError}
                                                    helperText={zone.helperText}
                                                    onBlur={() => {  }}
                                                />
                                            )}
                                            onChange={(args: any, newValue: any) => { onAutocompleteValueChanged(args, newValue, zone.pos); }}
                                            onInputChange={(args: any, newValue: any) => { onAutocompleteInputValueChanged(args, newValue, zone.pos, field.Fixed); }}
                                        />
                                    ) : field?.Type === "DATE" ? (
                                        <FormControl fullWidth>
                                            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="fr-ch">
                                            <DatePicker 
                                                value={dayjs(zone.value, "DD.MM.YYYY")}
                                                onChange={(args: any) => { onDateValueChanged(args, zone.pos); }}
                                                onAccept={() => {}}
                                                onError={() => {}}
                                                inputFormat="DD.MM.YYYY"
                                                inputRef={zone.ref} 
                                                renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label=""
                                                    required={field.Required}
                                                    error={zone.hasError}
                                                    helperText={zone.helperText}
                                                    value={zone.value}
                                                    onChange={(args: any) => { onDateTextValueChanged(args, zone.pos); }}
                                                    onBlur={() => {}}
                                                    variant="standard"
                                                />
                                                )}
                                            />
                                            </LocalizationProvider>
                                        </FormControl>
                                    ) : field?.Type === "NUMBER" ? (
                                        <FormControl fullWidth>
                                            <TextField
                                                inputRef={zone.ref}
                                                type="text"
                                                variant="standard"
                                                label=""
                                                required={field.Required}
                                                error={zone.hasError}
                                                helperText={zone.helperText}
                                                value={zone.value}
                                                onChange={(args: any) => { onNumberValueChanged(args, zone.pos); }}
                                                onBlur={(args: any) => { onNumberBlur(args, zone.pos); }}
                                                placeholder="0.00"
                                            />
                                            </FormControl>
                                    ) : (null);

                                    return (
                                    <TableRow key={zone.pos} onClick={() => onLineClick(zone.pos)} sx={{ backgroundColor: selectedPos == zone.pos ? "#e4f6fd" : (zone.pos == 0 || zone.pos == doctype?.PosRef) ? "#e5e5e5" : "transparent" }}>
                                        <TableCell sx={{ m: 0, p: 1 }}>{field?.Label}{field?.Required ? "*" : ""}</TableCell>
                                        <TableCell sx={{ m: 0, p: 1 }}>
                                            { (zone.pos > 0 && zone.pos !== doctype?.PosRef) ? (
                                                    <Select fullWidth 
                                                        size="small"
                                                        onChange={(args: any) => onOcrTypeChanged(args, zone.pos)}
                                                        value={zone.ocrType}>
                                                        <MenuItem value=""><em>Aucun</em></MenuItem>
                                                        <MenuItem value="C">Zone</MenuItem>
                                                        <MenuItem value="V">Valeur</MenuItem>
                                                    </Select>
                                            ) : zone.pos == doctype?.PosRef ? (
                                                <Typography>Valeur</Typography>
                                            ) : zone.pos === 0 && (
                                                "Contrôle"
                                            )}
                                        </TableCell>
                                        <TableCell sx={{ m: 0, p: 0 }} align="center">{zone.page > 0 && zone.page}</TableCell>
                                        <TableCell sx={{ m: 0, p: 1 }}>
                                            { zone.ocrType == "CTRL" && (
                                                <Typography>{zone.value}</Typography>
                                            ) }
                                            { zone.ocrType == "V" && zone.pos !== doctype?.PosRef && valueInputComponent }
                                            { zone.pos === doctype?.PosRef && (
                                                <Typography>{fieldRef?.InputValue}</Typography>
                                            )}
                                        </TableCell>
                                        <TableCell sx={{ m: 0, p: 0 }} align="center">
                                            { (zone.ocrType == "C" || zone.ocrType == "CTRL") && (
                                                <Tooltip title={t("templateCreator.resetCoordinates")} arrow placement="top">
                                                    <IconButton sx={{ p: 0 }} onClick={() => resetCoordinates(zone.pos)}>
                                                        <RestartAltIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            ) }
                                        </TableCell>
                                        <TableCell sx={{ m: 0, p: 0 }} align="center">
                                            { zone.pos !== doctype?.PosRef && (
                                                <Tooltip title={t("templateCreator.resetPosition")} arrow placement="top">
                                                    <IconButton sx={{ p: 0, pr: 1 }} onClick={() => zoneReset(zone.pos)}>
                                                        <CloseIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            ) }
                                        </TableCell>
                                    </TableRow>
                                )}) }
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                    <Box sx={{ pl: 1, flex: 1.5 }}>
                        <PdfViewerComponent 
                            ref={pdfviewer} 
                            resourceUrl={`${window.location.origin}/ej2-pdfviewer-lib`} 
                            style={{ height: "100%", width: "100%" }} 
                            annotationMove={onAnnotationMove} 
                            annotationResize={onAnnotationResize} 
                            annotationSelect={onAnnotationSelect}
                            zoomMode="FitToWidth" 
                            created={() => loadDocument()} 
                            documentLoad={onDocumentLoaded} 
                            enableToolbar={false} 
                            enableNavigationToolbar={false} 
                            enableAnnotationToolbar={false} 
                            enableCommentPanel={false}
                        >
                            <Inject
                                services={[
                                    Toolbar,
                                    Magnification,
                                    Navigation,
                                    ThumbnailView,
                                    TextSelection,
                                    TextSearch,
                                    Annotation,
                                    FormFields,
                                    FormDesigner,
                                    Print,
                                ]}
                            />
                        </PdfViewerComponent> 
                    </Box>
                </Box>

                <Box
                    sx={{
                        bgcolor: "background.paper",
                        px: 2,
                        pb: 2,
                        pt: 2,
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between"
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "flex-start",
                                alignItems: "center",
                            }}
                        >
                            <Box>
                                <Tooltip title={t("tooltip.save")} arrow placement="bottom">
                                    <IconButton color="info" onClick={() => saveTemplate()}>
                                        <SaveIcon />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>
                        <Box sx={{
                            display: "flex",
                            justifyContent: "flex-start",
                            alignItems: "center",
                        }}>
                            <Box>
                                <Tooltip
                                    title={t("button.cancel")}
                                    arrow
                                    placement="bottom"
                                >
                                    <IconButton color="error" onClick={() => onClose(hasChanges)}>
                                        <CancelIcon />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                            {deleteBtnVisible && (
                            <Box>
                                <Tooltip
                                    title={t("templateCreator.deleteTemplate")}
                                    arrow
                                    placement="bottom"
                                >
                                    <IconButton color="error" onClick={() => onDeleteTemplate()}>
                                        <DeleteIcon />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                            )}
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Modal>
    )
}

export default TemplateCreator;